import { apiDelete, apiGet, apiPut } from './api';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) || '';

export type FileRecord = {
  _id: string;
  originalName?: string;
  filename?: string;
  fileType?: string;
  fileSize?: number;
  mimeType?: string;
  thumbnailPath?: string;
  category?: string;
  tags?: string[];
  status?: string;
  uploadedAt?: string;
  uploadedBy?: { email?: string; nickname?: string } | string;
  url?: string;
};

export type FileStats = {
  totalFiles?: number;
  totalSize?: number;
  byType?: Record<string, number>;
};

export async function listFiles(params: Record<string, string | number> = {}) {
  const qs = new URLSearchParams(
    Object.entries(params).reduce((acc, [k, v]) => {
      if (v !== '' && v !== undefined) acc[k] = String(v);
      return acc;
    }, {} as Record<string, string>)
  ).toString();
  return apiGet<{
    success: boolean;
    data?: {
      files: FileRecord[];
      pagination?: {
        currentPage: number;
        totalPages: number;
        totalFiles: number;
        hasNext: boolean;
        hasPrev: boolean;
      };
    };
  }>(`/api/v1/files${qs ? `?${qs}` : ''}`);
}

export async function getFileStats() {
  return apiGet<{ success: boolean; data?: FileStats }>('/api/v1/files/stats/overview');
}

export async function updateFile(fileId: string, body: Record<string, unknown>) {
  return apiPut(`/api/v1/files/${fileId}`, body);
}

export async function deleteFile(fileId: string) {
  return apiDelete(`/api/v1/files/${fileId}`);
}

export type UploadFileResult = {
  success: boolean;
  message?: string;
  data?: {
    fileId: string;
    url?: string;
    originalName?: string;
  };
};

export async function uploadFile(
  file: File,
  options?: { tags?: string[] },
): Promise<UploadFileResult> {
  const formData = new FormData();
  formData.append('image', file);
  if (options?.tags?.length) {
    formData.append('tags', options.tags.join(','));
  }
  const res = await fetch(`${API_BASE_URL}/api/v1/files/images`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || `Upload failed (${res.status})`);
  }
  const fileId = data.data?.id || data.data?.fileId;
  return {
    ...data,
    data: {
      ...data.data,
      fileId,
      url: data.data?.url || (fileId ? `/api/v1/files/${fileId}/serve` : undefined),
    },
  };
}

export function fileServeUrl(fileId: string): string {
  return `${API_BASE_URL}/api/v1/files/${fileId}/serve`;
}

export async function fetchFileBlob(fileId: string): Promise<Blob> {
  const res = await fetch(fileServeUrl(fileId), { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`Failed to fetch file (${res.status})`);
  }
  return res.blob();
}
